import {Clickable, Portal} from 'bigdatr-style';
import {useViewer} from 'bigdatr-style';
import React, {useState} from 'react';
import {useCountry} from './CountryContext';
import {
    useFloating,
    useInteractions,
    useClick,
    useDismiss,
    autoUpdate,
    offset,
    shift,
    flip
} from '@floating-ui/react';
import useFeatureFlags from '~/util/useFeatureFlags';
import {
    CountryChoice,
    CountryDropdownFloater,
    CountryDropdownTrigger,
    CountryGroup
} from './CountryDropdownShared';
import {SupportedCountry, AU, NZ} from 'bigdatr-style';

enum FutureCountries {
    AR = 'Argentina',
    BE = 'Belgium',
    BR = 'Brazil',
    CA = 'Canada',
    CH = 'Switzerland',
    DE = 'Germany',
    DK = 'Denmark',
    ES = 'Spain',
    FI = 'Finland',
    FR = 'France',
    GB = 'United Kingdom',
    HK = 'Hong Kong',
    ID = 'Indonesia',
    IT = 'Italy',
    MX = 'Mexico',
    NL = 'Netherlands',
    NO = 'Norway',
    PH = 'Philippines',
    PR = 'Puerto Rico',
    RU = 'Russia',
    SE = 'Sweden',
    SG = 'Singapore',
    TW = 'Taiwan',
    US = 'United States',
    VN = 'Vietnam',
    ZA = 'South Africa'
}

export default function CountrySwitch() {
    const {country, setCountry} = useCountry();
    const {currentTeam} = useViewer();

    const {showJapaneseCreatives, showIndianCreatives} = useFeatureFlags();

    const changeCountry = (country: SupportedCountry) => {
        setCountry(country);
        setOpen(false);
    };

    const [open, setOpen] = useState(false);
    const {refs, floatingStyles, context} = useFloating({
        placement: 'bottom-end',
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,

        // order of middleware functions is important
        middleware: [
            offset(8),
            // keep floater in view if a horizontal scroll would make it offscreen
            shift(),
            // change floater placement to top of reference node if it would be offscreen when scrolling
            flip()
        ]
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);

    const {getFloatingProps, getReferenceProps} = useInteractions([click, dismiss]);

    const isAusAvailable =
        currentTeam.hasAusCreative || currentTeam.hasAusMediaValue || currentTeam.hasCompliance;
    const isNzAvailable = currentTeam.hasNzCreative;

    const japanOption = (
        <CountryChoice
            locked={!showJapaneseCreatives}
            country={SupportedCountry.JP}
            checked={country === SupportedCountry.JP}
            onChange={() => changeCountry(SupportedCountry.JP)}
        />
    );

    const indiaOption = (
        <CountryChoice
            locked={!showIndianCreatives}
            country={SupportedCountry.IN}
            checked={country === SupportedCountry.IN}
            onChange={() => changeCountry(SupportedCountry.IN)}
        />
    );

    return (
        <>
            <Clickable
                ref={refs.setReference}
                {...getReferenceProps()}
                data-testid="country-switcher"
            >
                <CountryDropdownTrigger country={country} open={open} />
            </Clickable>

            {open && (
                <Portal>
                    <CountryDropdownFloater
                        ref={refs.setFloating}
                        {...getFloatingProps()}
                        style={{...floatingStyles}}
                        p={3}
                    >
                        <CountryChoice
                            checked={country === AU}
                            onChange={() => changeCountry(AU)}
                            locked={!isAusAvailable}
                            country={AU}
                        />

                        <CountryChoice
                            checked={country === NZ}
                            onChange={() => changeCountry(NZ)}
                            locked={!isNzAvailable}
                            country={NZ}
                        />

                        {showJapaneseCreatives && japanOption}

                        {showIndianCreatives && indiaOption}

                        {/* North America & UK */}
                        <CountryChoice locked={true} country={FutureCountries.CA} />
                        <CountryChoice locked={true} country={FutureCountries.GB} />
                        <CountryChoice locked={true} country={FutureCountries.US} />

                        <CountryGroup region="Europe">
                            <CountryChoice locked={true} country={FutureCountries.BE} />
                            <CountryChoice locked={true} country={FutureCountries.DK} />
                            <CountryChoice locked={true} country={FutureCountries.FI} />
                            <CountryChoice locked={true} country={FutureCountries.FR} />
                            <CountryChoice locked={true} country={FutureCountries.DE} />
                            <CountryChoice locked={true} country={FutureCountries.IT} />
                            <CountryChoice locked={true} country={FutureCountries.NL} />
                            <CountryChoice locked={true} country={FutureCountries.NO} />
                            <CountryChoice locked={true} country={FutureCountries.RU} />
                            <CountryChoice locked={true} country={FutureCountries.ES} />
                            <CountryChoice locked={true} country={FutureCountries.SE} />
                            <CountryChoice locked={true} country={FutureCountries.CH} />
                        </CountryGroup>

                        <CountryGroup region="Asia Pacific">
                            <CountryChoice locked={true} country={FutureCountries.HK} />
                            {!showIndianCreatives && indiaOption}
                            <CountryChoice locked={true} country={FutureCountries.ID} />
                            {!showJapaneseCreatives && japanOption}
                            <CountryChoice locked={true} country={FutureCountries.PH} />
                            <CountryChoice locked={true} country={FutureCountries.SG} />
                            <CountryChoice locked={true} country={FutureCountries.TW} />
                            <CountryChoice locked={true} country={FutureCountries.VN} />
                        </CountryGroup>

                        <CountryGroup region="Americas">
                            <CountryChoice locked={true} country={FutureCountries.AR} />
                            <CountryChoice locked={true} country={FutureCountries.BR} />
                            <CountryChoice locked={true} country={FutureCountries.MX} />
                            <CountryChoice locked={true} country={FutureCountries.PR} />
                        </CountryGroup>

                        <CountryGroup region="Africa">
                            <CountryChoice locked={true} country={FutureCountries.ZA} />
                        </CountryGroup>
                    </CountryDropdownFloater>
                </Portal>
            )}
        </>
    );
}
